import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDate } from "./format";

// Continuous form 9.5" x 11/2" (5.5") = 241.3 x 139.7 mm, LANDSCAPE
// (dot-matrix printer friendly - simple black lines only)
export function generateDeliveryOrderPDF({ so, company, customer, preview }) {
  const doc = new jsPDF({ unit: "mm", format: [241.3, 139.7], orientation: "landscape" });
  const NAVY = [15, 23, 42];
  const BLUE = [30, 64, 175];
  const M = 6; // tight margin for continuous form
  const PW = 241.3;
  const PH = 139.7;

  // ---- Header LEFT: company ----
  let hx = M;
  if (company?.logo_base64) {
    try { doc.addImage(company.logo_base64, "PNG", M, M, 15, 15); hx = M + 18; } catch (e) {}
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...BLUE);
  doc.text((company?.company_name || "PT. FUJI INDO SURYA").toUpperCase(), hx, M + 4);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(60, 60, 60);
  const addr = doc.splitTextToSize(company?.address || "", 130);
  let ay = M + 8;
  addr.slice(0, 3).forEach((ln) => { doc.text(ln, hx, ay); ay += 3; });
  if (company?.phone) doc.text(`Ph.: ${company.phone}${company?.fax ? "  Fax: " + company.fax : ""}`, hx, ay);

  // ---- Header RIGHT: DO title + meta ----
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...BLUE);
  doc.text("DELIVERY ORDER", PW - M, M + 6, { align: "right" });
  doc.setFontSize(8);
  doc.setTextColor(...NAVY);
  doc.setFont("helvetica", "normal");
  const meta = [
    ["DO Number", so.do_number || so.so_number],
    ["Date", formatDate(new Date().toISOString())],
    ["PO Number", so.po_number || so.so_number],
  ];
  let my = M + 11;
  meta.forEach(([k, v]) => {
    doc.setFont("helvetica", "bold");
    doc.text(k, PW - M - 55, my);
    doc.setFont("helvetica", "normal");
    doc.text(`: ${v}`, PW - M - 30, my);
    my += 4;
  });

  // ---- Divider ----
  const dvY = Math.max(ay + 4, my + 2);
  doc.setDrawColor(...BLUE);
  doc.setLineWidth(0.4);
  doc.line(M, dvY, PW - M, dvY);

  // ---- Attention To ----
  let ry = dvY + 4;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...NAVY);
  doc.text("Attention To :", M, ry);
  ry += 4;
  doc.setFontSize(10);
  doc.text((customer?.name || so.customer_name || "-").toUpperCase(), M, ry);
  ry += 4;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(60, 60, 60);
  if (customer?.address) {
    const cl = doc.splitTextToSize(customer.address, 130);
    cl.slice(0, 2).forEach((ln) => { doc.text(ln, M, ry); ry += 3; });
  }

  // ---- Statement ----
  doc.setFontSize(7);
  doc.setTextColor(...NAVY);
  doc.setFont("helvetica", "italic");
  doc.text("Telah diterima barang dengan keadaan baik barang-barang di bawah ini :", M, ry + 2);

  // ---- Items table ----
  autoTable(doc, {
    startY: ry + 4,
    head: [["No.", "Description", "Unit", "Quantity", "Remark"]],
    body: so.items.map((it, i) => [
      String(i + 1),
      it.name,
      it.unit || "PCS",
      String(it.quantity),
      it.remark || "",
    ]),
    headStyles: { fillColor: BLUE, textColor: [255, 255, 255], fontSize: 8, halign: "center", fontStyle: "bold" },
    bodyStyles: { fontSize: 8, textColor: NAVY, lineColor: [120, 120, 120], lineWidth: 0.15 },
    columnStyles: {
      0: { cellWidth: 10, halign: "center" },
      1: { cellWidth: "auto" },
      2: { cellWidth: 18, halign: "center" },
      3: { cellWidth: 20, halign: "right", fontStyle: "bold" },
      4: { cellWidth: 40 },
    },
    margin: { left: M, right: M },
    theme: "grid",
  });

  // ---- Bottom section: remark + signatures ----
  let by = doc.lastAutoTable.finalY + 3;
  doc.setFontSize(7);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(...NAVY);
  doc.text("Remark: All goods has been received cannot be returned", M, by);

  // Signature boxes: Received | Created | Checked | Manager (proporsional, tinggi 22mm)
  const boxH = 22;
  const sigY = Math.min(by + 3, PH - boxH - 4);
  const boxW = (PW - M * 2) / 4 - 2;
  const labels = ["Received", "Created", "Checked", "Manager"];
  doc.setDrawColor(120, 120, 120);
  doc.setLineWidth(0.2);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...NAVY);
  labels.forEach((lbl, i) => {
    const x = M + i * (boxW + 2);
    doc.rect(x, sigY, boxW, boxH);
    doc.text(lbl, x + boxW / 2, sigY + 4, { align: "center" });
    // dotted line for name near bottom of box
    doc.setDrawColor(180, 180, 180);
    doc.setLineDashPattern([0.5, 0.5], 0);
    doc.line(x + 3, sigY + boxH - 3, x + boxW - 3, sigY + boxH - 3);
    doc.setLineDashPattern([], 0);
    doc.setDrawColor(120, 120, 120);
  });

  const filename = `SuratJalan_${(so.do_number || so.so_number).replaceAll("/", "-")}.pdf`;
  if (preview) return { url: doc.output("bloburl"), filename };
  doc.save(filename);
}
